import { format } from 'date-fns'
import { RRule } from 'rrule'

import { calculatePriority, detectCollision } from './priorityCalculator'
import { CalendarEvent } from '../context/Data'

export interface CalendarEventWithPlaylists extends CalendarEvent {
	playlists: Array<{
		id: number
		percentage: number
	}>
	volume?: number
}

export interface Slot {
	id?: number
	name: string
	type: string
	priority: number
	vevent: string
	timeslot_contents: Array<{
		id?: number
		type: string
		volume: number
		min_percentage?: number
		max_percentage?: number
		playlists: Array<{
			id: number
			percentage: number
		}>
	}>
}

export const convertEventToSlot = (
	event: CalendarEventWithPlaylists,
	allEvents: CalendarEventWithPlaylists[] = []
): Slot => {
	const vevent = generateVEvent(event)
	const priority = calculatePriority(event, allEvents)

	const timeslotId = (event as any).timeslotId

	// Check if it's a smart slot with timeslotContents
	if (
		(event as any).timeslotContents &&
		(event as any).timeslotContents.length > 0
	) {
		// Determine the smart slot type based on the variant
		let slotType = 'regular'
		if ((event as any).smartSlotConfig?.variant === 'weather') {
			slotType = 'weather'
		} else if ((event as any).smartSlotConfig?.variant === 'occupation') {
			slotType = 'occupation'
		}

		return {
			...(timeslotId ? { id: timeslotId } : {}),
			name: event.title,
			type: slotType,
			priority: priority,
			vevent: vevent,
			timeslot_contents: (event as any).timeslotContents,
		}
	}

	// Regular slot
	const timeslotContentId = (event as any).timeslotContentId

	return {
		...(timeslotId ? { id: timeslotId } : {}),
		name: event.title,
		type: 'regular',
		priority: priority,
		vevent: vevent,
		timeslot_contents: [
			{
				...(timeslotContentId ? { id: timeslotContentId } : {}),
				type: 'regular',
				volume: event.volume ?? 100,
				playlists: event.playlists,
			},
		],
	}
}

const generateVEvent = (event: CalendarEvent): string => {
	// Handle events with rrule.dtstart and duration (from event creation form)
	let start: Date
	let end: Date
	let durationMs: number

	if (event.rrule && event.rrule.dtstart && event.duration) {
		// Event from creation form with rrule
		start = new Date(event.rrule.dtstart)
		durationMs = event.duration.milliseconds || 0
		end = new Date(start.getTime() + durationMs)
	} else if (event.start && event.end) {
		// Event from FullCalendar (has start/end as Date objects)
		start = event.start instanceof Date ? event.start : new Date(event.start)
		end = event.end instanceof Date ? event.end : new Date(event.end)
		durationMs = end.getTime() - start.getTime()
	} else {
		console.error('Invalid date in event:', event)
		throw new Error(
			'Event must have either (start/end) or (rrule.dtstart/duration)'
		)
	}

	// Validate dates
	if (isNaN(start.getTime()) || isNaN(end.getTime())) {
		console.error('Invalid date in event:', event)
		throw new Error('Invalid date in event')
	}

	const dtstart = format(start, "yyyyMMdd'T'HHmmss")

	const hours = Math.floor(durationMs / (1000 * 60 * 60))
	const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60))
	const duration = `PT${hours}H${minutes > 0 ? minutes + 'M' : ''}`

	const veventLines = ['BEGIN:VEVENT', `DTSTART:${dtstart}`]

	if (event.rrule) {
		let rruleString = ''

		if (typeof event.rrule === 'string') {
			rruleString = event.rrule.replace('RRULE:', '')
		} else if (event.rrule.rrule) {
			// Event from FullCalendar with rrule.rrule string
			rruleString = event.rrule.rrule.replace('RRULE:', '')
		} else if (event.rrule.freq) {
			const rruleOptions: any = {}

			// Check if it's "everyday" by checking repeatDays or all 7 days selected
			const isEveryday =
				event.repeatDays?.includes('everyday') ||
				(event.rrule.byweekday &&
					Array.isArray(event.rrule.byweekday) &&
					event.rrule.byweekday.length === 7)

			// Map freq string or number to RRule constant
			// Force DAILY for all recurring events to maintain compatibility with older app versions
			rruleOptions.freq = (RRule as any).DAILY

			// Add byweekday if available and not everyday
			if (
				event.rrule.byweekday &&
				Array.isArray(event.rrule.byweekday) &&
				!isEveryday
			) {
				rruleOptions.byweekday = event.rrule.byweekday
			} else if (
				!isEveryday &&
				event.repeatDays &&
				event.repeatDays.length > 0
			) {
				// Fallback to repeatDays if byweekday is missing in the rrule object
				const dayMap: { [key: string]: number } = {
					monday: 0,
					tuesday: 1,
					wednesday: 2,
					thursday: 3,
					friday: 4,
					saturday: 5,
					sunday: 6,
				}
				rruleOptions.byweekday = event.repeatDays
					.map((day) => dayMap[day])
					.filter((d) => d !== undefined)
			}

			const rrule = new RRule(rruleOptions)
			rruleString = rrule.toString().replace('RRULE:', '')
		}

		if (rruleString) {
			if (event.rrule.until) {
				const untilDate = new Date(event.rrule.until)

				if (!isNaN(untilDate.getTime())) {
					const formattedUntil = format(untilDate, "yyyyMMdd'T'HHmmss")

					if (!rruleString.includes('UNTIL=')) {
						rruleString += `;UNTIL=${formattedUntil}`
					}
				}
			}

			veventLines.push(`RRULE:${rruleString}`)
		}
	} else if (event.repeatDays && event.repeatDays.length > 0) {
		if (event.repeatDays.includes('everyday')) {
			const rrule = new RRule({
				freq: RRule.DAILY,
			})
			const rruleString = rrule.toString().replace('RRULE:', '')
			veventLines.push(`RRULE:${rruleString}`)
		} else {
			const dayMap: { [key: string]: number } = {
				monday: 0,
				tuesday: 1,
				wednesday: 2,
				thursday: 3,
				friday: 4,
				saturday: 5,
				sunday: 6,
			}

			const byweekday = event.repeatDays
				.map((day) => dayMap[day])
				.filter((d) => d !== undefined)

			const rrule = new RRule({
				freq: (RRule as any).DAILY,
				byweekday: byweekday,
			})

			const rruleString = rrule.toString().replace('RRULE:', '')
			veventLines.push(`RRULE:${rruleString}`)
		}
	} else {
		const dtend = format(end, "yyyyMMdd'T'HHmmss")
		veventLines.push(`DTEND:${dtend}`)
	}

	if (event.rrule || (event.repeatDays && event.repeatDays.length > 0)) {
		veventLines.push(`DURATION:${duration}`)
	}

	veventLines.push('END:VEVENT')

	return veventLines.join('\n')
}

export const convertEventsToSlots = (
	events: CalendarEventWithPlaylists[]
): Slot[] => {
	const slots = events.map((event) => convertEventToSlot(event, events))

	const priorities = new Array(events.length).fill(0)

	for (let i = 0; i < events.length; i++) {
		let maxPriorityNeeded = 0

		for (let j = 0; j < events.length; j++) {
			if (i !== j && detectCollision(events[i], events[j])) {
				if (j < i) {
					maxPriorityNeeded = Math.max(maxPriorityNeeded, priorities[j] + 1)
				}
			}
		}

		priorities[i] = maxPriorityNeeded
	}

	const slotsWithAdjustedPriority = slots.map((slot, index) => ({
		...slot,
		priority: priorities[index],
	}))

	return slotsWithAdjustedPriority
}
